/**
 * SOLID Review
 * S — Strong. One concern: the ad slot as data — which provider the server's A/B split chose for whom, the
 *     request and its answer, and which zone the screen shows.
 * O — Good. A new provider is one enum value; a new zone one more value of `TClientAdZone`.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Only the ad moves.
 * D — Strong. zod only.
 */
import { z } from 'zod';

/**
 * THE AD SLOT (frontend strict-ports plan, FE4d, 2026-10-04). A free player's game screen has one ad area:
 * a cookie-free placeholder until advertising is consented to, then the provider the backend's A/B split
 * chose for this player (AdSense or Monetag). Premium players have no ad area at all. AppRoot asked for the
 * config with a hook and decided the zone in a ternary; both are the Domain's now.
 */

const ClientAdProvider = z.enum(['PROPELLER_ADS', 'ADSENSE', 'NONE']);

/** The provider and its slot, as the server answers `getAdProviderConfig` — what the client reads of it. */
const PayloadAdConfig = z.object({
  provider: ClientAdProvider,
  adsensePublisherId: z.string().nullable(),
  adsenseSlotId: z.string().nullable(),
}).meta({ id: 'PayloadAdConfig' });

/**
 * The session's ad config, and WHOM it was asked for: one request per player (the hook asked once per
 * mount; a sign-in as someone else is another player, with their own arm of the experiment).
 */
export const ClientAds = PayloadAdConfig.extend({ playerId: z.string().nullable() }).meta({ id: 'ClientAds' });

/** No config yet: the default provider the hook started from, asked for nobody. */
export const NO_ADS: z.infer<typeof ClientAds> = { playerId: null, provider: 'PROPELLER_ADS', adsensePublisherId: null, adsenseSlotId: null };

export const RequestAdConfig = z.object({ playerId: z.string() }).meta({ id: 'RequestAdConfig' });
/** The server's answer; null when it has none for this player. */
export const DtoAdConfig = z.object({ config: PayloadAdConfig.nullable() }).meta({ id: 'DtoAdConfig' });

// ── the decisions' commands ───────────────────────────────────────────────────────────────────

/** The request to make, or none: premium, no player yet, or already asked for this player. */
export const DtoAdConfigPlan = z.object({ request: RequestAdConfig.nullable() }).meta({ id: 'DtoAdConfigPlan' });
export const CommandAdConfigLoaded = z.object({ playerId: z.string(), config: PayloadAdConfig.nullable() }).meta({ id: 'CommandAdConfigLoaded' });

/** What the game screen's ad area shows. */
export type TClientAdZone = 'none' | 'placeholder' | 'adsense' | 'propeller';

export type IPayloadAdConfig = z.infer<typeof PayloadAdConfig>;
export type IEntityClientAds = z.infer<typeof ClientAds>;
export type IPayloadAdConfigRequest = z.infer<typeof RequestAdConfig>;
export type IEntityAdConfig = z.infer<typeof DtoAdConfig>;
export type IEntityAdConfigPlan = z.infer<typeof DtoAdConfigPlan>;
export type ICommandAdConfigLoaded = z.infer<typeof CommandAdConfigLoaded>;
